import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VerseReflectionModal = ({
  verse,
  isOpen,
  onClose,
  onSave,
  className = ''
}) => {
  const [isPrivate, setIsPrivate] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [reflections, setReflections] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPrompt, setRecordingPrompt] = useState(null);
  const [recognition, setRecognition] = useState(null);

  // Strip Strong's numbers from verse text
  const stripStrongsNumbers = (text) => {
    if (!text) return '';
    // Remove Strong's numbers in format: {H1234}, {G5678}, {(H8688)}, {(G1234)}, etc.
    return text?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.trim();
  };

  // Guided reflection prompts
  const prompts = [
    {
      id: 'insights',
      title: 'Personal Insights',
      question: 'What insights have you gained from thinking more specifically about this verse?',
      placeholder: 'Reflect on what this verse reveals to you personally...',
      icon: 'Lightbulb'
    },
    {
      id: 'actions',
      title: 'Holy Spirit Impressions',
      question: 'What actions is the Holy Spirit impressing upon you that you need to take today?',
      placeholder: 'Consider what God might be calling you to do...',
      icon: 'Zap'
    },
    {
      id: 'relationship',
      title: 'Christ & Church Connection',
      question: 'How does this verse relate to Christ, The Church, or you personally?',
      placeholder: 'Explore the connections to your faith journey...',
      icon: 'Heart'
    },
    {
      id: 'illustration',
      title: 'Nature & History Insights',
      question: 'What insight from nature or history relates to this verse as illustration or working out of these principles?',
      placeholder: 'Think of examples from the natural world or history...',
      icon: 'TreePine'
    },
    {
      id: 'habits',
      title: 'Habits to Release',
      question: 'Is there any habit you feel impressed to give up based on this verse?',
      placeholder: 'Consider what might be holding you back...',
      icon: 'Ban'
    },
    {
      id: 'testimony',
      title: 'Personal Testimony',
      question: 'Do you have a testimony related to this verse?',
      placeholder: 'Share how this verse has impacted your life...',
      icon: 'MessageCircle'
    },
    {
      id: 'general',
      title: 'Other Reflections',
      question: 'Is there anything else this verse brings to mind that doesn\'t fit the other categories?',
      placeholder: 'Share any other thoughts, prayers, or reflections this verse inspires...',
      icon: 'Edit3'
    }
  ];

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event) => {
        let transcript = '';
        for (let i = event?.resultIndex; i < event?.results?.length; i++) {
          if (event?.results?.[i]?.isFinal) {
            transcript += event?.results?.[i]?.[0]?.transcript;
          }
        }
        
        if (transcript && recordingPrompt) {
          setReflections(prev => ({
            ...prev,
            [recordingPrompt]: (prev?.[recordingPrompt] || '') + transcript + ' '
          }));
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event?.error);
        setIsRecording(false);
        setRecordingPrompt(null);
      };

      recognitionInstance.onend = () => {
        setIsRecording(false);
        setRecordingPrompt(null);
      };

      setRecognition(recognitionInstance);
    }
  }, [recordingPrompt]);

  // Load existing reflections if editing
  useEffect(() => {
    if (verse?.reflections) {
      setReflections(verse?.reflections);
      setIsPrivate(verse?.reflectionPrivacy === 'private');
    }
  }, [verse]);

  const handleStartRecording = (promptId) => {
    if (!recognition) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    if (isRecording) {
      recognition?.stop();
      return;
    }

    setRecordingPrompt(promptId);
    setIsRecording(true);
    recognition?.start();
  };

  const handleReflectionChange = (promptId, value) => {
    setReflections(prev => ({
      ...prev,
      [promptId]: value
    }));
  };

  const handleSave = () => {
    const reflection = {
      id: Date.now(),
      verseId: verse?.id,
      verseReference: verse?.reference,
      verseText: stripStrongsNumbers(verse?.text), // Strip Strong's numbers before saving
      reflections: reflections,
      isPrivate: isPrivate,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };

    onSave?.(reflection);
    onClose?.();
  };

  const handleClear = () => {
    setReflections({});
    setSelectedPrompt(null);
  };

  const filledPrompts = Object.keys(reflections)?.filter(key => {
    const value = reflections?.[key];
    return typeof value === 'string' && value?.trim();
  })?.length;
  const totalPrompts = prompts?.length;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-in fade-in duration-200"
        onClick={onClose}
      />
      {/* Modal */}
      <div className={`fixed inset-4 md:inset-8 lg:inset-16 bg-card rounded-lg shadow-warm-lg z-50 flex flex-col max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] animate-in fade-in zoom-in-95 duration-200 ${className}`}>
        {/* Header */}
        <div className="flex items-start justify-between p-4 md:p-6 border-b border-border flex-shrink-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-t-lg">
          <div className="flex-1 mr-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="BookOpen" size={20} className="text-primary" />
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Guided Reflection
              </h2>
            </div>
            <div className="bg-muted/30 p-3 md:p-4 rounded-lg mb-3">
              <h3 className="font-medium text-sm md:text-base text-foreground mb-1">{verse?.reference}</h3>
              <p className="text-xs md:text-sm text-foreground/80 leading-relaxed">
                "{stripStrongsNumbers(verse?.text)}"
              </p>
              <p className="text-xs text-muted-foreground mt-2">— {verse?.version || 'NIV'}</p>
            </div>
            <div className="flex items-center justify-between text-xs md:text-sm">
              <span className="text-muted-foreground">
                Progress: {filledPrompts} of {totalPrompts}
              </span>
              <div className="w-20 md:w-24 bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(filledPrompts / totalPrompts) * 100}%` }}
                />
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content - Made scrollable */}
        <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
          {/* Prompts Section - Redesigned with smaller buttons in 2 columns */}
          <div className="p-3 md:p-4 border-b border-border flex-shrink-0">
            <h3 className="font-medium text-sm md:text-base text-foreground mb-3">Reflection Prompts</h3>
            
            {/* Prompt Buttons Grid - 2 columns */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {prompts?.map((prompt) => {
                const isCompleted = reflections?.[prompt?.id]?.trim();
                const isActive = selectedPrompt === prompt?.id;
                
                return (
                  <button
                    key={prompt?.id}
                    onClick={() => setSelectedPrompt(prompt?.id)}
                    className={`text-left p-2 rounded-lg border transition-all duration-200 ${
                      isActive 
                        ? 'border-primary bg-primary/5 text-foreground' 
                        : 'border-border hover:border-primary/30 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`flex-shrink-0 p-1 rounded ${
                        isCompleted ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Icon 
                          name={isCompleted ? 'Check' : prompt?.icon} 
                          size={12} 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-xs text-foreground">
                          {prompt?.title}
                        </h4>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Details Area - Shows question for selected prompt */}
            {selectedPrompt && (
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 p-1.5 bg-primary/10 rounded">
                    <Icon 
                      name={prompts?.find(p => p?.id === selectedPrompt)?.icon} 
                      size={14} 
                      className="text-primary" 
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm text-foreground mb-1">
                      {prompts?.find(p => p?.id === selectedPrompt)?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {prompts?.find(p => p?.id === selectedPrompt)?.question}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Reflection Input Area */}
          <div className="flex-1 flex flex-col min-h-0">
            {selectedPrompt ? (
              <div className="flex-1 flex flex-col overflow-y-auto p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-foreground">
                    Your Reflection
                  </label>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStartRecording(selectedPrompt)}
                      className={`${
                        isRecording && recordingPrompt === selectedPrompt 
                          ? 'bg-destructive text-destructive-foreground' 
                          : ''
                      }`}
                      disabled={!recognition}
                    >
                      <Icon 
                        name={isRecording && recordingPrompt === selectedPrompt ? 'MicOff' : 'Mic'} 
                        size={14} 
                      />
                      <span className="hidden sm:inline ml-1">
                        {isRecording && recordingPrompt === selectedPrompt ? 'Stop' : 'Voice'}
                      </span>
                    </Button>
                  </div>
                </div>
                
                <textarea
                  value={reflections?.[selectedPrompt] || ''}
                  onChange={(e) => handleReflectionChange(selectedPrompt, e?.target?.value)}
                  placeholder={prompts?.find(p => p?.id === selectedPrompt)?.placeholder}
                  className="w-full flex-1 min-h-[200px] p-3 md:p-4 border border-border rounded-lg resize-none text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
                
                {isRecording && recordingPrompt === selectedPrompt && (
                  <div className="flex items-center space-x-2 mt-2 text-xs md:text-sm text-muted-foreground">
                    <div className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
                    <span>Recording... Speak clearly into your microphone</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-4 md:p-6">
                <div className="text-center max-w-md">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="BookOpen" size={24} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
                    Begin Your Reflection
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-4">
                    Select a prompt above to start your guided meditation on this verse. 
                    Each prompt will help you explore different aspects of the Scripture.
                  </p>
                  <Button
                    onClick={() => setSelectedPrompt(prompts?.[0]?.id)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Start with First Prompt
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Made flex-shrink-0 to prevent collapsing */}
        <div className="p-4 md:p-6 border-t border-border flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            {/* Privacy Toggle */}
            <div className="flex items-center space-x-2 md:space-x-3">
              <span className="text-xs md:text-sm font-medium text-foreground">Visibility:</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsPrivate(true)}
                  className={`px-2 md:px-3 py-1.5 text-xs md:text-sm rounded-md border transition-colors duration-200 ${
                    isPrivate 
                      ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Lock" size={12} className="inline mr-1" />
                  Private
                </button>
                <button
                  onClick={() => setIsPrivate(false)}
                  className={`px-2 md:px-3 py-1.5 text-xs md:text-sm rounded-md border transition-colors duration-200 ${
                    !isPrivate 
                      ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Globe" size={12} className="inline mr-1" />
                  Public
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 md:space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={filledPrompts === 0}
                className="text-xs md:text-sm"
              >
                Clear All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                className="text-xs md:text-sm"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={filledPrompts === 0}
                className="bg-primary hover:bg-primary/90 text-xs md:text-sm"
              >
                Save
              </Button>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mt-3 p-2 md:p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground">
              {isPrivate 
                ? "🔒 This reflection will only be visible to you and can be accessed from your personal library." :"🌍 This reflection will be visible to the community and may appear in public verse displays to encourage others."
              }
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerseReflectionModal;